/**
 * A model for notifications
 * If you pass a `progressTask` object containing a `uuid` to the attributes at creation, batch task polling will happen automatically
 * @class
 */
import Base from '@netx/core/lib/netx-model';
import {batchJobs} from '@netx/core-app/collections/singleton/batchJobs';
import {getUserLevel} from '@netx/core-user-next/utils/getUserLevel';

export default class Notification extends Base {
	/**
	 * @property {object}  defaults             - The defaults for this model
	 * @property {number}  defaults.id            - The default id
	 * @property {date}    defaults.creationTime  - The default creation time
	 * @property {array}   defaults.ids           - The default ids related
	 * @property {string}  defaults.module        - The default module related
	 * @property {string}  defaults.route         - Default link to add to notification
	 * @property {string}  defaults.routeLabel    - Default text for the link
	 * @property {string}  defaults.title         - The default title
	 * @property {string}  defaults.type          - The default type (danger, info, success, warning)
	 * @property {object}  defaults.progressTask  - The default `progressTask` object (if this is related to a batch task polling)
	 * @property {array}   defaults.progressTask.args       - The default arguments to pass to batch task polling
	 * @property {string}  defaults.progressTask.eventStr   - The default eventStr to pass to batch task polling
	 * @property {boolean} defaults.progressTask.hasResult  - The default hasResult to pass to batch task polling
	 * @property {string}  defaults.progressTask.uuid       - The default uuid to pass to batch task polling (without this, no polling will occur)
	 * @property {number}  defaults.progressTask.progress   - The default progress of the batch task polling (this when when a task progresses)
	 * @property {object}  defaults.progressTask.completed  - The default `completed` object
	 * @property {string}  defaults.progressTask.completed.error      - The default error string for batch task (only set if error occurred while polling)
	 * @property {mixed}   defaults.progressTask.completed.shareData  - The default `shareData` for batch task (only set if the batch task had results)
	 * @property {number}  defaults.progressTask.completed.time       - The default time of completion
	 */
	get defaults() {
		return {
			id: _.uniqueId(_.now()),
			creationTime: _.now(),
			description: '',
			ids: [],
			module: '',
			route: '',
			routeLabel: '',
			title: '',
			type: '',
			progressTask: {},
		};
	}

	/**
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 * @fires module:app#app:pollBatchJob
	 */
	constructor(attrs, opts) {
		super(attrs, opts);

		if (getUserLevel() < 0) {
			this.listenToOnce(dispatcher, 'auth:session:valid', () => {
				this.checkForProgress();
			});
		} else {
			_.defer(() => {
				this.checkForProgress();
			});
		}
	}

	/** @method */
	checkForProgress() {
		if (getUserLevel() > 0) {
			var progressTask = this.get('progressTask');
			if (!_.isEmpty(progressTask) && _.isUndefined(progressTask.completed)) {
				// Persist
				this.storeOptions.persist = true;

				var batchJob = batchJobs.get(progressTask.uuid);
				// Doesn't exist yet
				if (!batchJob) {
					// Poll the job
					dispatcher.trigger(
						'app:pollBatchJob',
						progressTask.uuid,
						progressTask.eventStr,
						progressTask.hasResult,
						progressTask.args,
					);
					// There should now be a betch job model for this job
					batchJob = batchJobs.get(progressTask.uuid);
				}

				if (batchJob) {
					// Listen for progress
					this.listenTo(
						batchJob,
						'change:percentComplete',
						function (batchJob, percentComplete) {
							progressTask.progress = percentComplete;
							this.trigger('progress', this, percentComplete);
						},
					);
					// Listen once for an error
					this.listenToOnce(batchJob, 'error', function (batchJob, err) {
						// Set time of completion and the error message
						progressTask.completed = {
							time: new Date().getTime(),
							error: err,
							completed: true,
						};
						this.trigger('error', this, err, batchJob);
						// Objects only register changes once
						this.trigger('change', this);
					});
					// Listen once for complete
					this.listenToOnce(batchJob, 'complete', function (batchJob) {
						// Set time of completion and any shareData related
						progressTask.completed = {
							time: new Date().getTime(),
							shareData: batchJob.get('shareData'),
							completed: true,
						};
						this.trigger('complete', this, batchJob);
						// Objects only register changes once
						this.trigger('change', this);
					});
				}
			}
		}
	}

	/** @type {string} */
	static get __name__() {
		return 'Notification';
	}
}
